import { createContext } from 'react'
import type { LoginResponse, Usuario } from '../api/types'
import type { LoginInput } from './authApi'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

/**
 * Resultado da primeira etapa do login.
 *
 * Com segundo fator ativo, a senha correta não cria sessão: devolve `desafio`,
 * que sozinho não autentica nada. A sessão só nasce em `completarMfa`. Modelar
 * como união obriga quem chama a tratar os dois casos, em vez de assumir que
 * login sempre devolve usuário.
 */
export type ResultadoLogin =
  | { tipo: 'sessao'; usuario: Usuario; configurarMfa: boolean; senhaTemporaria: boolean }
  | { tipo: 'mfa'; desafio: string; expiraEm: number }

export interface AuthContextValue {
  user: Usuario | null
  status: AuthStatus
  /** A sessão foi aberta com a senha temporária do e-mail: só a criação da
   *  senha definitiva está liberada, no backend e na navegação. */
  precisaCriarSenha: boolean
  login: (input: LoginInput) => Promise<ResultadoLogin>
  completarMfa: (desafio: string, codigo: string) => Promise<Usuario>
  /** Aplica a sessão devolvida por quem grava uma senha nova. Sem isto, o bump
   *  de token_version derrubaria a aba que acabou de criar a senha. */
  aplicarSessao: (resposta: LoginResponse) => Usuario
  /** Recarrega o usuário logado, com as permissões do momento. A sessão era lida
   *  uma vez só, na montagem: revogar permissão de quem está logado não surtia
   *  efeito na interface até a pessoa recarregar a página. */
  revalidar: () => Promise<void>
  logout: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)
